import { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Instagram, Facebook, Youtube, ShoppingBag, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import BanyanMark from "@/components/BanyanMark";
import Modal from "@/components/Modal";
import { scrollToId } from "@/lib/scroll";
import { sendInquiry, trackClick } from "@/lib/api";
import { AMAZON_STORE, BOOKS } from "@/data/books";

const TikTokIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1 0-5.78c.3 0 .58.05.85.13V9.01a6.34 6.34 0 0 0-.85-.05 6.32 6.32 0 1 0 6.33 6.32V8.75a8.16 8.16 0 0 0 4.77 1.52V6.84a4.85 4.85 0 0 1-1-.15z" />
  </svg>
);

const SOCIALS = [
  { label: "YouTube — Banyan Tree Press stories", href: "https://www.youtube.com/channel/UCIOdYZDaejW4qKL9eljLfmA", Icon: Youtube, testId: "social-youtube" },
  { label: "Instagram — @banyantree.press", href: "https://www.instagram.com/banyantree.press/", Icon: Instagram, testId: "social-instagram" },
  { label: "TikTok — @banyantreepress", href: "https://www.tiktok.com/@banyantreepress", Icon: TikTokIcon, testId: "social-tiktok" },
  { label: "Facebook — Banyan Tree Press", href: "https://www.facebook.com/profile.php?id=61588754416492", Icon: Facebook, testId: "social-facebook" },
];

const LEGAL_TEXT = {
  privacy: [
    "Banyan Tree Press (visitbanyantree.com) collects only the information you voluntarily provide — typically a name and email address when joining our reader list or contacting us.",
    "We use this information solely to deliver the free Activity Pack, share new-release news from the Panchatantra Tales series, and respond to inquiries. We never sell, rent, or share your data with third parties for marketing.",
    "Outbound links to Amazon are provided for your convenience; Amazon's own privacy policy applies on their platform. You may request deletion of your data at any time via the Contact form.",
  ],
  terms: [
    "All content on this site — including text, artwork, the Banyan Tree Press and Arlo Jungle names, and the Panchatantra Tales series branding — is © 2026 Banyan Tree Press. All rights reserved.",
    "The free Coloring & Activity Pack is licensed for personal, non-commercial family and classroom use. It may not be resold or redistributed.",
    "Book purchases are fulfilled by Amazon under their terms of sale. Banyan Tree Press is responsible for the creative content of the books only.",
  ],
};

export default function Footer() {
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", inquiry_type: "rights", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submitInquiry = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please complete all fields.");
      return;
    }
    setSending(true);
    try {
      await sendInquiry(form);
      toast.success("Inquiry sent — we'll reply within two business days.");
      setForm({ name: "", email: "", inquiry_type: "rights", message: "" });
      setModal(null);
    } catch {
      toast.error("Could not send your inquiry. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <footer className="border-t border-gold/15 bg-emerald-ink" data-testid="site-footer">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
        <div className="grid gap-12 lg:grid-cols-[1.3fr_1fr_1fr_1.2fr]">
          <div>
            <div className="flex items-center gap-3">
              <img
                src="/logo-192.webp"
                alt="Banyan Books logo"
                className="h-11 w-11 rounded-full ring-1 ring-gold/50"
              />
              <div>
                <p className="font-serif text-xl font-semibold text-cream">Banyan Tree Press</p>
                <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-gold/75">
                  Ancient Wisdom · Modern Magic
                </p>
              </div>
            </div>
            <p className="mt-5 max-w-sm text-sm leading-relaxed text-cream/60">
              Reimagining the 2,000-year-old Panchatantra for Generation Alpha —
              cinematic art, timeless morals, six languages, one great banyan tree.
            </p>
            <div className="mt-6 flex gap-3">
              {SOCIALS.map(({ label, href, Icon, testId }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  data-testid={testId}
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-cream/15 text-cream/70 transition-all duration-300 hover:border-gold hover:text-gold"
                >
                  <Icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </div>

          <nav aria-label="Explore" data-testid="footer-explore">
            <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-gold/80">Explore</p>
            <ul className="mt-5 space-y-3 text-sm">
              {[
                { label: "The Library", id: "library", t: "footer-link-library" },
                { label: "The Panchatantra", route: "/panchatantra", t: "footer-link-panchatantra" },
                { label: "Reader Reviews", id: "reviews", t: "footer-link-reviews" },
                { label: "The Manifesto", id: "manifesto", t: "footer-link-manifesto" },
                { label: "Free Activity Pack", id: "activity-pack", t: "footer-link-activity" },
                { label: "Global Reach", id: "global", t: "footer-link-global" },
                { label: "Contact Us", route: "/contact", t: "footer-link-contact" },
                { label: "For Publishers", route: "/publishers", t: "footer-link-publishers" },
              ].map((l) => (
                <li key={l.t}>
                  {l.route ? (
                    <RouterLink
                      to={l.route}
                      data-testid={l.t}
                      className="text-cream/65 transition-colors hover:text-gold"
                    >
                      {l.label}
                    </RouterLink>
                  ) : (
                    <button
                      onClick={() => scrollToId(l.id)}
                      data-testid={l.t}
                      className="text-cream/65 transition-colors hover:text-gold"
                    >
                      {l.label}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </nav>

          <nav aria-label="The series" data-testid="footer-series">
            <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-gold/80">The Series</p>
            <ul className="mt-5 space-y-3 text-sm">
              {BOOKS.map((b) => (
                <li key={b.id}>
                  <a
                    href={b.amazon}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`footer-book-${b.id}`}
                    onClick={() => trackClick(`footer-${b.id}`, b.amazon, "footer")}
                    className="text-cream/65 transition-colors hover:text-gold"
                  >
                    {b.title}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-gold/80">The Bookstore</p>
            <a
              href={AMAZON_STORE}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-amazon-store-btn"
              onClick={() => trackClick("footer-amazon-store", AMAZON_STORE, "footer")}
              className="mt-5 flex w-fit items-center gap-2.5 rounded-full bg-gold px-6 py-3 font-mono text-xs font-semibold uppercase tracking-[0.15em] text-emerald-ink transition-transform duration-300 hover:-translate-y-0.5 hover:shadow-glow"
            >
              <ShoppingBag className="h-4 w-4" />
              Amazon Author Central
            </a>
            <p className="mt-4 text-sm leading-relaxed text-cream/55">
              Every edition — paperback, hardcover, and all six languages — ships
              worldwide via Amazon.
            </p>
          </div>
        </div>

        <div className="mt-14 flex flex-col items-start justify-between gap-6 border-t border-gold/12 pt-8 sm:flex-row sm:items-center">
          <p className="text-xs text-cream/50" data-testid="footer-copyright">
            © 2026 Banyan Tree Press. All rights reserved. · visitbanyantree.com
          </p>
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            <button
              onClick={() => setModal("privacy")}
              data-testid="footer-privacy-link"
              className="text-xs text-cream/60 underline-offset-4 transition-colors hover:text-gold hover:underline"
            >
              Privacy Policy
            </button>
            <button
              onClick={() => setModal("terms")}
              data-testid="footer-terms-link"
              className="text-xs text-cream/60 underline-offset-4 transition-colors hover:text-gold hover:underline"
            >
              Terms of Service
            </button>
            <button
              onClick={() => setModal("contact")}
              data-testid="footer-contact-link"
              className="text-xs text-cream/60 underline-offset-4 transition-colors hover:text-gold hover:underline"
            >
              Contact / Rights Inquiries
            </button>
          </div>
        </div>
      </div>

      <Modal open={modal === "privacy"} onClose={() => setModal(null)} title="Privacy Policy" testId="privacy-modal">
        <div className="space-y-4 text-sm leading-relaxed text-cream/75">
          {LEGAL_TEXT.privacy.map((p) => <p key={p.slice(0, 24)}>{p}</p>)}
        </div>
      </Modal>

      <Modal open={modal === "terms"} onClose={() => setModal(null)} title="Terms of Service" testId="terms-modal">
        <div className="space-y-4 text-sm leading-relaxed text-cream/75">
          {LEGAL_TEXT.terms.map((p) => <p key={p.slice(0, 24)}>{p}</p>)}
        </div>
      </Modal>

      <Modal open={modal === "contact"} onClose={() => setModal(null)} title="Contact / Rights Inquiries" testId="contact-modal">
        <form onSubmit={submitInquiry} className="space-y-4" data-testid="contact-form">
          <div>
            <label htmlFor="cf-name" className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.22em] text-gold/85">Name</label>
            <input
              id="cf-name"
              type="text"
              value={form.name}
              onChange={set("name")}
              data-testid="contact-name-input"
              className="w-full rounded-sm border border-cream/20 bg-emerald-ink/70 px-4 py-3 text-sm text-cream placeholder:text-cream/35 focus:border-gold focus:outline-none"
              placeholder="Your name"
            />
          </div>
          <div>
            <label htmlFor="cf-email" className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.22em] text-gold/85">Email</label>
            <input
              id="cf-email"
              type="email"
              value={form.email}
              onChange={set("email")}
              data-testid="contact-email-input"
              className="w-full rounded-sm border border-cream/20 bg-emerald-ink/70 px-4 py-3 text-sm text-cream placeholder:text-cream/35 focus:border-gold focus:outline-none"
              placeholder="you@example.com"
            />
          </div>
          <div>
            <label htmlFor="cf-type" className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.22em] text-gold/85">Inquiry Type</label>
            <select
              id="cf-type"
              value={form.inquiry_type}
              onChange={set("inquiry_type")}
              data-testid="contact-type-select"
              className="w-full rounded-sm border border-cream/20 bg-emerald-ink/70 px-4 py-3 text-sm text-cream focus:border-gold focus:outline-none"
            >
              <option value="rights">Rights & Licensing</option>
              <option value="press">Press & Media</option>
              <option value="schools">Schools & Libraries</option>
              <option value="general">General</option>
            </select>
          </div>
          <div>
            <label htmlFor="cf-message" className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.22em] text-gold/85">Message</label>
            <textarea
              id="cf-message"
              rows={4}
              value={form.message}
              onChange={set("message")}
              data-testid="contact-message-input"
              className="w-full resize-none rounded-sm border border-cream/20 bg-emerald-ink/70 px-4 py-3 text-sm text-cream placeholder:text-cream/35 focus:border-gold focus:outline-none"
              placeholder="Tell us about your inquiry…"
            />
          </div>
          <button
            type="submit"
            disabled={sending}
            data-testid="contact-submit-btn"
            className="flex w-full items-center justify-center gap-2 rounded-full bg-gold px-6 py-3.5 font-mono text-xs font-semibold uppercase tracking-[0.15em] text-emerald-ink transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-60"
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Send Inquiry
          </button>
        </form>
      </Modal>
    </footer>
  );
}
